/**
 * useAdvancedSettings - Hook pour gérer les paramètres avancés avec persistence
 * Adresse, services connectés, préférences musicales, horaires, etc.
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'adhd_planner_advanced_settings';

const DEFAULT_SETTINGS = {
  // Adresse utilisateur
  address: {
    street: '46 bis Avenue Jean Jaurès',
    city: 'Paris',
    postalCode: '75019',
    country: 'France',
  },

  // Horaires quotidiens
  schedule: {
    wakeTime: '07:00',
    sleepTime: '23:00',
    breakfastTime: '08:00',
    lunchTime: '12:30',
    dinnerTime: '19:30',
    workStartTime: '09:00',
    workEndTime: '18:00',
  },

  // Préférences d'assistance
  assistance: {
    level: 'normal', // 'minimal', 'normal', 'maximum'
    enableVoiceReminders: false,
    enableHapticFeedback: true,
    subtaskDetail: 'detailed', // 'simple', 'detailed', 'comprehensive'
    encouragementFrequency: 'medium', // 'low', 'medium', 'high'
  },

  // Services connectés
  connectedServices: {
    spotify: {
      enabled: false,
      accessToken: null,
      refreshToken: null,
      preferences: {
        morningPlaylist: 'focus',
        workPlaylist: 'productivity',
        exercisePlaylist: 'energetic',
        relaxPlaylist: 'calm',
        sleepPlaylist: 'sleep',
      },
    },
    youtube: {
      enabled: false,
      preferences: {
        preferShortVideos: true,
        categories: ['meditation', 'exercise', 'cooking', 'education'],
      },
    },
    primeVideo: {
      enabled: false,
      preferences: {
        allowAsReward: true,
        maxDuration: 30, // minutes
      },
    },
  },

  // Génération automatique de semaine
  autoGeneration: {
    enabled: false,
    template: 'balanced', // 'minimal', 'balanced', 'structured', 'intense'
    includeWeekends: true,
    autoAddMeals: true,
    autoAddHygiene: true,
    autoAddBreaks: true,
    customRoutines: [],
  },

  // Préférences de catégories
  categoryPreferences: {
    vital: {
      color: '#ef4444',
      emoji: '❤️',
      defaultDuration: 30,
      priority: 1,
    },
    chore: {
      color: '#f59e0b',
      emoji: '🔧',
      defaultDuration: 45,
      priority: 2,
    },
    pleasure: {
      color: '#10b981',
      emoji: '✨',
      defaultDuration: 30,
      priority: 3,
    },
    reward: {
      color: '#8b5cf6',
      emoji: '🎁',
      defaultDuration: 20,
      priority: 4,
    },
  },

  // Liens intelligents préférés
  smartLinks: {
    enableRecipeSuggestions: true,
    enablePlaceSuggestions: true,
    enablePlaylistSuggestions: true,
    preferredRecipeSites: ['marmiton', 'cuisineaz', '750g'],
    preferredMusicService: 'spotify', // 'spotify', 'youtube', 'apple'
    preferredMapService: 'google', // 'google', 'apple', 'waze'
  },

  // Gamification
  gamification: {
    enabled: true,
    showXP: true,
    showLevel: true,
    enableStreaks: true,
    enableChallenges: true,
    dailyGoal: 5, // nombre de tâches
    weeklyGoal: 25,
  },
};

export function useAdvancedSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Charger les paramètres au montage
  useEffect(() => {
    loadSettings();
  }, []);

  // Sauvegarder automatiquement quand les settings changent
  useEffect(() => {
    if (!isLoading && hasChanges) {
      saveSettings();
    }
  }, [settings, hasChanges, isLoading]);

  const loadSettings = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge avec les valeurs par défaut pour les nouveaux champs
        setSettings(deepMerge(DEFAULT_SETTINGS, parsed));
      }
    } catch (error) {
      console.error('Erreur chargement paramètres avancés:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveSettings = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
    }
  }, [settings]);

  // Mettre à jour un chemin de paramètre (ex: 'address.street')
  const updateSetting = useCallback((path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setHasChanges(true);
  }, []);

  // Mettre à jour plusieurs paramètres à la fois
  const updateSettings = useCallback((updates) => {
    setSettings(prev => deepMerge(prev, updates));
    setHasChanges(true);
  }, []);

  // Réinitialiser un groupe de paramètres
  const resetSection = useCallback((sectionKey) => {
    if (DEFAULT_SETTINGS[sectionKey]) {
      setSettings(prev => ({
        ...prev,
        [sectionKey]: { ...DEFAULT_SETTINGS[sectionKey] },
      }));
      setHasChanges(true);
    }
  }, []);

  // Réinitialiser tous les paramètres
  const resetAll = useCallback(() => {
    setSettings({ ...DEFAULT_SETTINGS });
    setHasChanges(true);
  }, []);

  // Obtenir l'adresse formatée
  const getFormattedAddress = useCallback(() => {
    const { street, city, postalCode, country } = settings.address;
    return `${street}, ${postalCode} ${city}, ${country}`;
  }, [settings.address]);

  // Vérifier si un service est connecté
  const isServiceConnected = useCallback((serviceName) => {
    return settings.connectedServices[serviceName]?.enabled || false;
  }, [settings.connectedServices]);

  // Connecter un service
  const connectService = useCallback((serviceName, credentials = {}) => {
    updateSettings({
      connectedServices: {
        [serviceName]: {
          ...settings.connectedServices[serviceName],
          enabled: true,
          ...credentials,
        },
      },
    });
  }, [settings.connectedServices, updateSettings]);

  // Déconnecter un service
  const disconnectService = useCallback((serviceName) => {
    updateSettings({
      connectedServices: {
        [serviceName]: {
          ...DEFAULT_SETTINGS.connectedServices[serviceName],
          enabled: false,
        },
      },
    });
  }, [updateSettings]);

  // Obtenir la playlist appropriée pour une activité
  const getPlaylistForActivity = useCallback((activityType) => {
    const spotify = settings.connectedServices.spotify;
    if (!spotify.enabled) return null;

    const mapping = {
      morning: spotify.preferences.morningPlaylist,
      work: spotify.preferences.workPlaylist,
      exercise: spotify.preferences.exercisePlaylist,
      relax: spotify.preferences.relaxPlaylist,
      sleep: spotify.preferences.sleepPlaylist,
    };

    return mapping[activityType] || 'focus';
  }, [settings.connectedServices.spotify]);

  return {
    settings,
    isLoading,
    hasChanges,

    // Actions
    updateSetting,
    updateSettings,
    resetSection,
    resetAll,
    saveSettings,

    // Helpers
    getFormattedAddress,
    isServiceConnected,
    connectService,
    disconnectService,
    getPlaylistForActivity,

    // Direct access
    address: settings.address,
    schedule: settings.schedule,
    assistance: settings.assistance,
    connectedServices: settings.connectedServices,
    autoGeneration: settings.autoGeneration,
    categoryPreferences: settings.categoryPreferences,
    smartLinks: settings.smartLinks,
    gamification: settings.gamification,
  };
}

// Utilitaire pour merge profond
function deepMerge(target, source) {
  const result = { ...target };

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }

  return result;
}

export default useAdvancedSettings;
